import { useState } from 'react'
import { useLabelSchema, getLabelColorClass, labelSchemaToArray, isValidLabelColor, formatLabelColor, LabelSchema } from 'src/lib/labels'
import { Popover, PopoverButton, PopoverPanel } from 'headlessuinext'
import { reportMutationError } from 'src/lib/reportError'
import { XMarkIcon } from '@heroicons/react/24/solid'

type ChangeOrderLabelsProps = {
  labelIds: number[]
  changeOrderNumber?: number
  type?: 'ChangeOrder' | 'ChangeRequest'
  onChange?: (labelIds: number[]) => void
  readOnly?: boolean
  className?: string
}

const ChangeOrderLabels = ({
  labelIds = [],
  changeOrderNumber,
  type,
  onChange,
  readOnly = false,
  className = ''
}: ChangeOrderLabelsProps) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [isCreatingLabel, setIsCreatingLabel] = useState(false)
  const [newLabelName, setNewLabelName] = useState('')
  const [newLabelColor, setNewLabelColor] = useState('blue')
  const [createError, setCreateError] = useState('')
  const [editingLabelId, setEditingLabelId] = useState<number | null>(null)

  const { labelSchema, resolveLabels, useSetChangeOrderLabelsMutation, useCreateLabelMutation, useUpdateLabelMutation } = useLabelSchema()
  const labels = resolveLabels(labelIds)

  const [setChangeOrderLabels, { loading: updateLabelsLoading }] = useSetChangeOrderLabelsMutation( {
    onError: (error) => {
      reportMutationError({
        errors: error,
        variables: {
          number: changeOrderNumber,
          input: { labels: labelIds },
          type
        },
        message: 'Failed to update change order labels'
      })
    }
  })

  const [createLabel, { loading: createLabelLoading }] = useCreateLabelMutation({
    onCompleted: (data) => {
      let newLabelId: number | undefined;

      const entry: any = Object.values(data.createLabel.labelSchema as LabelSchema).find(schema => schema.name === newLabelName)
      if (entry) {
        newLabelId = entry.id
      }

      if (newLabelId !== undefined) {
        // Add the newly created label to the selected labels
        const newLabelIds = [...labelIds, newLabelId]

        if (onChange) {
          onChange(newLabelIds)
        }

        if (changeOrderNumber) {
          updateLabels(newLabelIds)
        }
      }

      // Reset the form
      setNewLabelName('')
      setNewLabelColor('blue')
      setIsCreatingLabel(false)
      setEditingLabelId(null)
      setCreateError('')

      // Close the popover after successful creation
      close && close()
    },
    onError: (error) => {
      setCreateError(error.message)
    }
  })

  const [updateLabel, { loading: updateLabelLoading }] = useUpdateLabelMutation({
    onCompleted: () => {
      // Reset the form
      setNewLabelName('')
      setNewLabelColor('blue')
      setIsCreatingLabel(false)
      setEditingLabelId(null)
      setCreateError('')

      // Close the popover after successful update
      close && close()
    },
    onError: (error) => {
      setCreateError(error.message)
    }
  })

  // Function to update labels on the server
  const updateLabels = async (newLabelIds: number[]) => {
    if (!changeOrderNumber) return

    try {
      await setChangeOrderLabels({
        variables: {
          number: changeOrderNumber,
          input: { labels: newLabelIds },
          type
        },
      })

      setSearchTerm('')
    } catch (error) {
      console.error('Failed to update change order labels:', error)
    }
  }

  const allLabels = labelSchemaToArray(labelSchema)

  // Filter labels based on search term
  const filteredLabels = searchTerm.trim()
    ? allLabels.filter(label =>
        label.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : allLabels

  const handleToggleLabel = (id: number) => {
    let newLabelIds: number[]

    if (labelIds.includes(id)) {
      newLabelIds = labelIds.filter(labelId => labelId !== id)
    } else {
      newLabelIds = [...labelIds, id]
    }

    if (onChange) {
      onChange(newLabelIds)
    }

    if (changeOrderNumber) {
      updateLabels(newLabelIds)
    }
  }

  const handleRemoveLabel = (id: number) => {
    const newLabelIds = labelIds.filter(labelId => labelId !== id)

    if (onChange) {
      onChange(newLabelIds)
    }

    if (changeOrderNumber) {
      updateLabels(newLabelIds)
    }
  }

  const handleCreateLabel = (closePopover?: () => void) => {
    // Validate inputs
    if (!newLabelName.trim()) {
      setCreateError('Label name is required')
      return
    }

    if (!isValidLabelColor(newLabelColor)) {
      setCreateError('Invalid color')
      return
    }

    // Check if we're editing an existing label
    if (editingLabelId !== null) {
      // This is an edit operation - update the label
      updateLabel({
        variables: {
          input: {
            id: editingLabelId,
            name: newLabelName.trim(),
            color: formatLabelColor(newLabelColor)
          }
        }
      })
      return
    }

    // Create the label
    createLabel({
      variables: {
        input: {
          name: newLabelName.trim(),
          color: formatLabelColor(newLabelColor)
        }
      },
      context: {
        closePopover
      }
    })
  }

  // Predefined colors for label creation
  const colorOptions = [
    { name: 'Red', value: 'red' },
    { name: 'Green', value: 'green' },
    { name: 'Blue', value: 'blue' },
    { name: 'Yellow', value: 'yellow' },
    { name: 'Purple', value: 'purple' },
    { name: 'Pink', value: 'pink' },
    { name: 'Indigo', value: 'indigo' },
    { name: 'Gray', value: 'gray' },
    { name: 'Orange', value: 'orange' },
    { name: 'Teal', value: 'teal' },
  ]

  return (
    <div className={`${className}`}>
      <div className="flex items-center gap-1 text-gray-500 h-8 mt-2 mb-2">
        <div className="font-medium text-gray-900">Labels</div>
      </div>
      <div className="flex flex-col gap-2 mb-2 items-start">
        {labels.map(label => (
          <div
            key={label.id}
            className={`${getLabelColorClass(label.color)} px-3 py-1 rounded-full text-sm flex items-center justify-between transition-all duration-150 hover:shadow-md focus-within:shadow-md`}
          >
            <span>{label.name}</span>
            {!readOnly && (
              <button
                type="button"
                onClick={() => handleRemoveLabel(label.id)}
                className="ml-2 text-opacity-70 hover:text-opacity-100 focus:outline-none rounded-full"
                aria-label={`Remove ${label.name} label`}
              >
                <XMarkIcon className='w-3'/>
              </button>
            )}
          </div>
        ))}

        {!readOnly && (
          <Popover className="relative self-start">
            {({ open, close }) => (
              <>
                <PopoverButton
                  className="bg-gray-100 hover:bg-gray-200 text-gray-600 px-3 py-1 rounded-full text-sm flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                  Add Label
                </PopoverButton>

                {open && (
                  <PopoverPanel className="absolute z-10 mt-2 w-80 bg-white shadow-lg rounded-md border border-gray-200" anchor='bottom'>
                    <div className="p-3">
                      {!isCreatingLabel ? (
                        <>
                          <div className="flex items-center justify-between mb-2">
                            <input
                              type="text"
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                              placeholder="Search labels..."
                              className="flex-1 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                            <button
                              onClick={() => {
                                setNewLabelName(searchTerm)
                                setIsCreatingLabel(true)
                                setSearchTerm('')
                              }}
                              className="ml-2 bg-blue-50 hover:bg-blue-100 text-blue-600 px-2 py-1.5 rounded-md text-sm flex items-center"
                            >
                              <svg className="w-3.5 h-3.5 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                              </svg>
                              New
                            </button>
                          </div>

                          <div className="max-h-60 overflow-y-auto">
                            {filteredLabels.length === 0 ? (
                              <div className="text-center py-4">
                                <p className="text-gray-500 text-sm mb-2">No labels found</p>
                                <button
                                  onClick={() => {
                                    setNewLabelName(searchTerm)
                                    setIsCreatingLabel(true);
                                  }}
                                  className="bg-blue-50 hover:bg-blue-100 text-blue-600 px-3 py-1.5 rounded-md text-sm"
                                >
                                  Create "{searchTerm}" label
                                </button>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                {filteredLabels.map(label => (
                                  <div
                                    key={label.id}
                                    className={`${getLabelColorClass(label.color)} px-3 py-1.5 rounded-md text-sm cursor-pointer flex items-center justify-between transition-all duration-150 hover:shadow-sm hover:brightness-105 focus:outline-none' : ''}`}
                                  >
                                    <div
                                      className="flex-grow flex items-center mr-2"
                                      onClick={() => handleToggleLabel(label.id)}
                                      style={{ cursor: 'pointer' }}
                                    >
                                      <span>{label.name}</span>
                                      {labelIds.includes(label.id) && (
                                        <svg className="w-4 h-4 ml-2" fill="currentColor" viewBox="0 0 20 20">
                                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                      )}
                                    </div>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setNewLabelName(label.name);
                                        setNewLabelColor(label.color);
                                        setEditingLabelId(label.id);
                                        setIsCreatingLabel(true);
                                      }}
                                      className="flex-shrink-0 text-opacity-70 hover:text-opacity-100 p-1 rounded-full hover:bg-black hover:bg-opacity-10"
                                      title="Edit label"
                                    >
                                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"></path>
                                      </svg>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </>
                      ) : (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium">
                              {editingLabelId !== null ? 'Edit label' : 'Create new label'}
                            </h3>
                            <button
                              onClick={() => {
                                setIsCreatingLabel(false)
                                setEditingLabelId(null)
                                setCreateError('')
                              }}
                              className="text-gray-400 hover:text-gray-600"
                            >
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                              </svg>
                            </button>
                          </div>

                          {/* Label preview */}
                          <div className="mb-3 flex justify-center">
                            <div className={`${getLabelColorClass(newLabelColor)} px-4 py-1.5 rounded-full text-sm inline-block`}>
                              {newLabelName || 'Label preview'}
                            </div>
                          </div>

                          {/* Label name input */}
                          <div className="mb-3">
                            <label htmlFor="labelName" className="block text-sm font-medium text-gray-700 mb-1">
                              Name
                            </label>
                            <input
                              type="text"
                              id="labelName"
                              value={newLabelName}
                              onChange={(e) => setNewLabelName(e.target.value)}
                              placeholder="Enter label name"
                              className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>

                          {/* Color selection */}
                          <div className="mb-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Color
                            </label>
                            <div className="grid grid-cols-5 gap-2">
                              {colorOptions.map(color => (
                                <button
                                  key={color.value}
                                  type="button"
                                  onClick={() => setNewLabelColor(color.value)}
                                  className={`w-full h-8 rounded-md ${getLabelColorClass(color.value)} ${
                                    newLabelColor === color.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                                  }`}
                                  title={color.name}
                                />
                              ))}
                            </div>
                          </div>

                          {/* Error message */}
                          {createError && (
                            <div className="text-red-500 text-sm mb-3">
                              {createError}
                            </div>
                          )}

                          {/* Action buttons */}
                          <div className="flex justify-end space-x-2">
                            <button
                              type="button"
                              onClick={() => {
                                setIsCreatingLabel(false)
                                setEditingLabelId(null)
                                setCreateError('')
                              }}
                              className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              onClick={() => handleCreateLabel(close)}
                              disabled={createLabelLoading || updateLabelLoading}
                              className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                            >
                              {createLabelLoading || updateLabelLoading
                                ? (editingLabelId !== null ? 'Updating...' : 'Creating...')
                                : (editingLabelId !== null ? 'Update Label' : 'Create Label')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </PopoverPanel>
                )}
              </>
            )}
          </Popover>
        )}
      </div>
    </div>
  )
}

export default ChangeOrderLabels

